import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AnimatedIntro } from '@sorare/core/src/components/animations/AnimatedStory/AnimatedIntro';

import { GameweekDate } from '#src/components/so5/LastFixtureStory/GameweekDate/index.tsx';
import { Cta } from '#src/components/so5/LastFixtureStory/LastFixtureStoryStep/Cta/index.tsx';
import { useRewardsVerification } from '#src/components/so5/LastFixtureStory/RewardsRecap/useRewardsVerification.ts';

import type {
  Intro_so5Fixture,
  Intro_so5Reward,
} from './__generated__/index.graphql.ts';

const gameweekEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const GameweekDateWrapper = styled(Vertical).attrs({ center: true })`
  animation: ${gameweekEnter} 2s ease-out forwards;
`;

type Props = {
  onClose: () => Promise<void>;
  onNext?: () => void;
  so5Fixture?: Intro_so5Fixture;
  so5Rewards: Intro_so5Reward[];
};
export const Intro = ({ so5Fixture, so5Rewards, onNext, onClose }: Props) => {
  const { unclaimedRewardsByType } = useRewardsVerification(so5Rewards);
  const [isUnmounting, setIsUnmounting] = useState(false);
  const actionAfterOutro = async (action?: () => Promise<void>) => {
    return new Promise<void>(resolve => {
      setIsUnmounting(true);
      if (!action) return;
      setTimeout(async () => {
        await action();
        resolve();
      }, 1000);
    });
  };

  return (
    <AnimatedIntro
      isUnmounting={isUnmounting}
      setIsUnmounting={setIsUnmounting}
      headlinePill={
        !!so5Fixture && (
          <GameweekDateWrapper>
            <GameweekDate
              startDate={so5Fixture.startDate}
              endDate={so5Fixture.endDate}
            />
          </GameweekDateWrapper>
        )
      }
      title={
        <>
          {onNext ? (
            <>
              {unclaimedRewardsByType.total > 0 ? (
                <FormattedMessage
                  id="LastFixtureStory.titleWithRewards"
                  defaultMessage="Claim your glory"
                />
              ) : (
                <FormattedMessage
                  id="LastFixtureStory.title"
                  defaultMessage="New Pro Results"
                />
              )}
            </>
          ) : (
            <FormattedMessage
              id="LastFixtureStory.mainTitle"
              defaultMessage="A Game Week has ended{br}time to check your results"
              values={{ br: <br /> }}
            />
          )}
        </>
      }
      buttons={
        <>
          {onNext ? (
            <Button size="medium" color="secondary" fullWidth onClick={onNext}>
              <FormattedMessage
                id="LastFixtureStory.viewNow"
                defaultMessage="View Now"
              />
            </Button>
          ) : (
            <Cta
              so5FixtureSlug={so5Fixture?.slug}
              eventType={so5Fixture?.type}
              onClose={async () => actionAfterOutro(onClose)}
            />
          )}
        </>
      }
      onClose={onClose}
    />
  );
};

Intro.fragments = {
  so5Reward: gql`
    fragment Intro_so5Reward on So5Reward {
      slug
      aasmState
      ...useRewardsVerification_so5Reward
    }
    ${useRewardsVerification.fragments.so5Reward}
  ` as TypedDocumentNode<Intro_so5Reward>,
  so5Fixture: gql`
    fragment Intro_so5Fixture on So5Fixture {
      slug
      type
      startDate
      endDate
    }
  ` as TypedDocumentNode<Intro_so5Fixture>,
};
